/** Bonsai OS iframe + host messaging protocol (versioned). */

import { postOsMessageToParent } from "./embedHosts";

export const OS_PROTOCOL_VERSION = 1;

export const OS_GUEST_READY = "bonsai-os:guest-ready";
export const OS_HANDSHAKE_REQUEST = "bonsai-os:handshake-request";
export const OS_HANDSHAKE = "bonsai-os:handshake";
export const OS_THEME = "bonsai-os:theme";
export const OS_NAVIGATE = "bonsai-os:navigate";
export const OS_TOAST = "bonsai-os:toast";
/** Unified OS notification from guest apps / host → Notification Center. */
export const OS_NOTIFY = "bonsai-os:notify";
/** Guest → host unread mirror for dock badge fan-in (no full list required). */
export const OS_NOTIFY_MIRROR = "bonsai-os:notify-mirror";
export const OS_OPEN_HUB_WIDGET = "bonsai-os:open-hub-widget";
export const OS_BAZAAR_PATH = "bonsai-os:bazaar-path";
export const OS_WALLET_SYNC = "bonsai-os:wallet-sync";
/**
 * Legacy wire: guest asked host for OISY connect.
 * Phase 1 cutover: hosts should treat as II connect; `requestOsHostOisyConnect` aliases to II.
 * Live OISY remains only in `ic-nft-migrator` (inbound migration → Canopy / II).
 */
export const OS_OISY_CONNECT = "bonsai-os:oisy-connect";
/** Guest iframe asks host to run Internet Identity (delegation lives on host). */
export const OS_II_CONNECT = "bonsai-os:ii-connect";
/** Guest iframe asks host to delegate II to guest session key (embed / popout). */
export const OS_II_DELEGATION_REQUEST = "bonsai-os:ii-delegation-request";
/** Guest iframe asks host to run Stoic (Toniq) connect popup on top-level page. */
export const OS_STOIC_CONNECT = "bonsai-os:stoic-connect";
/** Guest asks host to set the active ICP wallet picker (ii | oisy | stoic). */
export const OS_ICP_WALLET_SELECT = "bonsai-os:icp-wallet-select";
/** Guest iframe asks host to run EVM / Phantom / HashPack connect (popups on top-level OS). */
export const OS_MULTICHAIN_CONNECT = "bonsai-os:multichain-connect";

export type IcpWalletKind = "ii" | "oisy" | "stoic";
export type MultichainWalletKind = "evm" | "solana" | "hedera";

/** Radio iframe → Registry OS shell lock-screen metadata relay. */
export const OS_RADIO_MEDIA = "bonsai-radio:media";

/** Host shell: visible outcome card (toast rail above hub). */
export const OS_FLYWHEEL_PULSE = "bonsai-os:flywheel-pulse";

export const OS_APP_HEARTBEAT = "bonsai-os:app-heartbeat";

export type OsAppHeartbeatEvent = "open" | "session" | "wallet_connect";

export type OsAppHeartbeatPayload = {
  appId: string;
  event: OsAppHeartbeatEvent;
  chain?: string;
  durationSec?: number;
};

export type OsAppHeartbeatMessage = {
  type: typeof OS_APP_HEARTBEAT;
  heartbeat: OsAppHeartbeatPayload;
};

export type OsFlywheelPulsePayload = {
  id?: string;
  title: string;
  body: string;
  imageUrl?: string;
  /** hub widget id, external url, or route path */
  primaryLabel: string;
  primaryTarget: { kind: "hub"; widgetId: string; path?: string } | { kind: "url"; href: string } | { kind: "route"; path: string };
  secondaryLabel?: string;
  secondaryTarget?: { kind: "hub"; widgetId: string; path?: string } | { kind: "url"; href: string } | { kind: "route"; path: string };
};

export type OsFlywheelPulseMessage = {
  type: typeof OS_FLYWHEEL_PULSE;
  pulse: OsFlywheelPulsePayload;
};

/** Hub iframe guest → Bonsai OS host: embed loaded and framed successfully. */
export const HUB_EMBED_READY = "bonsai-hub-embed-ready";
/** Hub host → guest: request (re-)send of HUB_EMBED_READY / OS_GUEST_READY. */
export const HUB_EMBED_REQUEST = "bonsai-hub-embed-request";

export type OsEmbedMode = "standalone" | "os" | "registry" | "market";

export type OsLinkedWallet = {
  chain: string;
  address: string;
  verified?: boolean;
  /** EVM numeric chain id when known (e.g. 1, 8453). */
  chainId?: number;
};

export type OsSession = {
  protocolVersion: number;
  hostOrigin: string;
  /** Internet Identity principal when signed in. */
  principal: string | null;
  /** JSON-serialized DelegationChain for guest canister calls (embed apps). */
  delegationChainJson?: string | null;
  /**
   * Wire name kept for embed compatibility.
   * Hosts should populate from the II principal (dual-write during OISY→Canopy cutover).
   * Do not require a live OISY session for day-to-day wallet use.
   */
  oisyPrincipal: string | null;
  stoicPrincipal: string | null;
  /** User-selected ICP wallet for transactions and NFT scans. */
  activeIcpWallet: IcpWalletKind | null;
  linkedWallets: OsLinkedWallet[];
  theme: string;
  capabilities: string[];
  /**
   * Host Data Saver / Lite preference. Guests should gate media preload,
   * autoplay, and prefetch when true. Absent on older hosts → treat as false.
   */
  dataSaver?: boolean;
};

export type OsGuestReadyMessage = {
  type: typeof OS_GUEST_READY;
  appId: string;
  embedMode: OsEmbedMode;
};

export type OsHandshakeMessage = {
  type: typeof OS_HANDSHAKE;
  session: OsSession;
};

export type OsThemeMessage = {
  type: typeof OS_THEME;
  theme: string;
};

export type OsNavigateMessage = {
  type: typeof OS_NAVIGATE;
  appId: string;
  path: string;
};

export type OsOpenHubWidgetMessage = {
  type: typeof OS_OPEN_HUB_WIDGET;
  widgetId: string;
  path?: string;
};

export type OsOpenHubWidgetDetail = {
  widgetId: string;
  path?: string;
};

type OsOpenHubWidgetWindow = Window & {
  __bonsaiOsPendingOpenHubWidget?: OsOpenHubWidgetDetail | null;
  __bonsaiOsOpenHubWidgetListenerCount?: number;
};

/**
 * Open a Hub widget from constellation / holo / deep links.
 * Stashes the latest request so a lazily-mounted Hub dock can replay it
 * (landing shell often fires opens before BonsaiHubDock finishes loading).
 */
export function dispatchOsOpenHubWidget(widgetId: string, path?: string): void {
  if (typeof window === "undefined") return;
  const id = widgetId.trim();
  if (!id) return;
  const detail: OsOpenHubWidgetDetail = path ? { widgetId: id, path } : { widgetId: id };
  const w = window as OsOpenHubWidgetWindow;
  w.__bonsaiOsPendingOpenHubWidget = detail;
  window.dispatchEvent(new CustomEvent(OS_OPEN_HUB_WIDGET, { detail }));
}

/** Take (and clear) the last open request if Hub was not listening yet. */
export function consumePendingOsOpenHubWidget(): OsOpenHubWidgetDetail | null {
  if (typeof window === "undefined") return null;
  const w = window as OsOpenHubWidgetWindow;
  const pending = w.__bonsaiOsPendingOpenHubWidget ?? null;
  w.__bonsaiOsPendingOpenHubWidget = null;
  return pending;
}

/** Peek without clearing — useful while the dock is still booting widgets. */
export function peekPendingOsOpenHubWidget(): OsOpenHubWidgetDetail | null {
  if (typeof window === "undefined") return null;
  return (window as OsOpenHubWidgetWindow).__bonsaiOsPendingOpenHubWidget ?? null;
}

export type OsBazaarPathMessage = {
  type: typeof OS_BAZAAR_PATH;
  path: string;
};

export type OsWalletSyncMessage = {
  type: typeof OS_WALLET_SYNC;
  session: Pick<
    OsSession,
    | "principal"
    | "delegationChainJson"
    | "oisyPrincipal"
    | "stoicPrincipal"
    | "activeIcpWallet"
    | "linkedWallets"
  >;
};

export type OsIiDelegationRequestMessage = {
  type: typeof OS_II_DELEGATION_REQUEST;
  appId?: string;
  /** base64(DER) of guest Ed25519 session public key */
  publicKeyDerBase64: string;
};
export type OsIcpWalletSelectMessage = {
  type: typeof OS_ICP_WALLET_SELECT;
  kind: IcpWalletKind;
};

export type OsMultichainConnectMessage = {
  type: typeof OS_MULTICHAIN_CONNECT;
  kind: MultichainWalletKind;
};

export type OsRadioMediaPayload = {
  title: string;
  artist: string;
  album?: string;
  artwork?: string;
};

export type OsRadioMediaMessage = {
  type: typeof OS_RADIO_MEDIA;
  playing: boolean;
  currentTime: number;
  duration: number;
  track: OsRadioMediaPayload | null;
};

/** Hub tray tile hover ↔ landing constellation node pulse. */
export const OS_HUB_TILE_HOVER = "bonsai-os:hub-tile-hover";
/** Constellation node hover → Hub tray highlight. */
export const OS_CONSTELLATION_HOVER = "bonsai-os:constellation-hover";
/** Host session slice for constellation status rings. */
export const OS_SESSION_MIRROR = "bonsai-os:session-mirror";
/** Open / close the Hub app tray (Core node, FAB parity). */
export const OS_OPEN_HUB_TRAY = "bonsai-os:open-hub-tray";
/**
 * Live Hub folder layout from Registry admin → landing constellation/holo.
 * Same folders, assignments, labels, and icon URLs as the Hub tray.
 */
export const OS_HUB_FOLDER_MIRROR = "bonsai-os:hub-folder-mirror";

export type OsHoverBridgePayload = {
  widgetId: string | null;
};

export type OsSessionMirrorPayload = {
  signedIn: boolean;
  /** Caller Registry avatar when signed in (Core PFP). */
  avatarUrl?: string | null;
};

/** Friend node for landing constellation rings (Top 3 inner + Next 5 outer). */
export type OsFriendConstellationNode = {
  principal: string;
  label: string;
  avatarUrl?: string;
  ring: "inner" | "outer";
  activeLevel?: "off" | "active" | "hot";
  /** Optional activity blurb when pulsing. */
  activityTitle?: string;
  activityBody?: string;
  activityHref?: string;
  activityHubTarget?: { widgetId: string; path?: string };
};

export type OsFriendConstellationPayload = {
  friends: OsFriendConstellationNode[];
};

/** Live friend constellation from Hub → landing WebGL. */
export const OS_FRIEND_CONSTELLATION = "bonsai-os:friend-constellation";

/**
 * Profile iframe (Registry origin) → bonsaios.app host: Top-8 pin prefs changed.
 * Host Bridge applies pins + refetches Motoko; landing rings update without reload.
 */
export const OS_CONSTELLATION_PREFS = "bonsai-os:constellation-prefs";

export type OsConstellationPrefsPayload = {
  innerPins: string[];
  outerPins: string[];
};

export type OsFriendConstellationMessage = {
  type: typeof OS_FRIEND_CONSTELLATION;
  friends: OsFriendConstellationPayload;
};

export type OsConstellationPrefsMessage = {
  type: typeof OS_CONSTELLATION_PREFS;
  prefs: OsConstellationPrefsPayload;
};

/** One agentified meme (Fleet Meme Desk → constellation shuffle). */
export type OsAgentifiedMeme = {
  id: string;
  agentId: string;
  callsign: string;
  title: string;
  /** Caption / body shown in constellation popup */
  body: string;
  createdAt: number;
  hue?: string;
};

export type OsMemeConstellationPayload = {
  memes: OsAgentifiedMeme[];
  /** When set, constellation should flash this meme immediately (notify popup). */
  highlightId?: string;
};

/** Full shuffle deck for landing constellation. */
export const OS_MEME_CONSTELLATION = "bonsai-os:meme-constellation";

/** Upsert a single meme from Fleet (iframe → Hub host). */
export const OS_MEME_AGENTIFIED = "bonsai-os:meme-agentified";

export type OsMemeConstellationMessage = {
  type: typeof OS_MEME_CONSTELLATION;
  memes: OsMemeConstellationPayload;
};

export type OsMemeAgentifiedMessage = {
  type: typeof OS_MEME_AGENTIFIED;
  meme: OsAgentifiedMeme;
  /** Also fire OS notify popup when true (default true). */
  notify?: boolean;
};

export type OsNotifySeverity = "info" | "attention" | "urgent";

export type OsNotifyPayload = {
  appId: string;
  id: string;
  title: string;
  body: string;
  severity?: OsNotifySeverity;
  createdAt: number;
  href?: string;
  hubTarget?: { widgetId: string; path?: string };
  actorPrincipal?: string;
  read?: boolean;
  /** friend_activity | social | tube | system | … */
  kind?: string;
};

export type OsNotifyMirrorPayload = {
  appId: string;
  unreadCount: number;
  latest?: OsNotifyPayload[];
};

export type OsNotifyMessage = {
  type: typeof OS_NOTIFY;
  notify: OsNotifyPayload;
};

export type OsOpenHubTrayPayload = {
  /** Defaults to true (open). Pass false to close. */
  open?: boolean;
};

export type OsHubFolderMirrorApp = {
  id: string;
  label: string;
  shortLabel: string;
  kind: "widget" | "wallet" | "folder";
  /** Resolved image URL when available (suite/custom/platform logos). */
  iconSrc?: string;
};

export type OsHubFolderMirrorFolder = {
  id: string;
  label: string;
  shortLabel: string;
  accent: string;
  accentHex: number;
  apps: OsHubFolderMirrorApp[];
};

export type OsHubFolderMirrorPayload = {
  folders: OsHubFolderMirrorFolder[];
  /** True when layout came from on-chain public Hub config (admin dashboard). */
  fromChain: boolean;
};

export function dispatchOsHubTileHover(widgetId: string | null): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OS_HUB_TILE_HOVER, { detail: { widgetId } satisfies OsHoverBridgePayload }),
  );
}

export function dispatchOsConstellationHover(widgetId: string | null): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OS_CONSTELLATION_HOVER, {
      detail: { widgetId } satisfies OsHoverBridgePayload,
    }),
  );
}

export function dispatchOsSessionMirror(payload: OsSessionMirrorPayload): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OS_SESSION_MIRROR, { detail: payload }));
}

export function dispatchOsOpenHubTray(payload: OsOpenHubTrayPayload = { open: true }): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OS_OPEN_HUB_TRAY, {
      detail: { open: payload.open !== false } satisfies OsOpenHubTrayPayload,
    }),
  );
}

export function dispatchOsHubFolderMirror(payload: OsHubFolderMirrorPayload): void {
  if (typeof window === "undefined") return;
  (window as Window & { __bonsaiOsHubFolderMirror?: OsHubFolderMirrorPayload }).__bonsaiOsHubFolderMirror =
    payload;
  window.dispatchEvent(
    new CustomEvent(OS_HUB_FOLDER_MIRROR, {
      detail: payload satisfies OsHubFolderMirrorPayload,
    }),
  );
}

function postToOsParent(message: Record<string, unknown>): void {
  postOsMessageToParent(message);
}

export function dispatchOsFriendConstellation(payload: OsFriendConstellationPayload): void {
  if (typeof window === "undefined") return;
  (window as Window & { __bonsaiOsFriendConstellation?: OsFriendConstellationPayload }).__bonsaiOsFriendConstellation =
    payload;
  window.dispatchEvent(
    new CustomEvent(OS_FRIEND_CONSTELLATION, {
      detail: payload satisfies OsFriendConstellationPayload,
    }),
  );
  // Profile hub panel is a cross-origin Registry iframe on bonsaios.app — fan-in to host.
  postToOsParent({
    type: OS_FRIEND_CONSTELLATION,
    friends: payload,
  } satisfies OsFriendConstellationMessage);
}

/** Notify OS host that Top-8 pins changed (iframe → parent). */
export function dispatchOsConstellationPrefs(prefs: OsConstellationPrefsPayload): void {
  if (typeof window === "undefined") return;
  const next: OsConstellationPrefsPayload = {
    innerPins: prefs.innerPins.slice(0, 3),
    outerPins: prefs.outerPins.slice(0, 5),
  };
  window.dispatchEvent(
    new CustomEvent(OS_CONSTELLATION_PREFS, {
      detail: next satisfies OsConstellationPrefsPayload,
    }),
  );
  postToOsParent({
    type: OS_CONSTELLATION_PREFS,
    prefs: next,
  } satisfies OsConstellationPrefsMessage);
}

export function peekOsFriendConstellation(): OsFriendConstellationPayload | null {
  if (typeof window === "undefined") return null;
  return (
    (window as Window & { __bonsaiOsFriendConstellation?: OsFriendConstellationPayload })
      .__bonsaiOsFriendConstellation ?? null
  );
}

export function dispatchOsMemeConstellation(payload: OsMemeConstellationPayload): void {
  if (typeof window === "undefined") return;
  (window as Window & { __bonsaiOsMemeConstellation?: OsMemeConstellationPayload }).__bonsaiOsMemeConstellation =
    payload;
  window.dispatchEvent(
    new CustomEvent(OS_MEME_CONSTELLATION, {
      detail: payload satisfies OsMemeConstellationPayload,
    }),
  );
  postOsMessageToParent(
    { type: OS_MEME_CONSTELLATION, memes: payload } satisfies OsMemeConstellationMessage,
  );
}

export function peekOsMemeConstellation(): OsMemeConstellationPayload | null {
  if (typeof window === "undefined") return null;
  return (
    (window as Window & { __bonsaiOsMemeConstellation?: OsMemeConstellationPayload })
      .__bonsaiOsMemeConstellation ?? null
  );
}

/** Fleet → Hub: persist + reshuffle constellation memes. */
export function dispatchOsMemeAgentified(meme: OsAgentifiedMeme, notify = true): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OS_MEME_AGENTIFIED, {
      detail: { meme, notify } satisfies { meme: OsAgentifiedMeme; notify: boolean },
    }),
  );
  postOsMessageToParent(
    { type: OS_MEME_AGENTIFIED, meme, notify } satisfies OsMemeAgentifiedMessage,
  );
}

export function dispatchOsNotify(payload: OsNotifyPayload): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OS_NOTIFY, { detail: payload satisfies OsNotifyPayload }),
  );
  // Also postMessage for iframe → host fan-in
  postOsMessageToParent({ type: OS_NOTIFY, notify: payload } satisfies OsNotifyMessage);
}

export function dispatchOsNotifyMirror(payload: OsNotifyMirrorPayload): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OS_NOTIFY_MIRROR, { detail: payload }));
  postOsMessageToParent({ type: OS_NOTIFY_MIRROR, mirror: payload });
}

export function isOsMessage(data: unknown): data is { type: string } {
  return typeof data === "object" && data !== null && typeof (data as { type?: unknown }).type === "string";
}
